import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { NewPassword, EyeCloseIcon, EyeIcon, Tick } from "asset/icons";
import Input from "component/form/Input";
import axios from "axios";
import ErrorMessage from "./ErrorMessage";
import Loader from "component/loader";

const CreateNewPassword = () => {
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [passwordValidations, setPasswordValidations] = useState({
        uppercase: false,
        lowercase: false,
        number: false,
        specialChar: false,
        minLength: false,
    });

    const navigate = useNavigate();
    const { state } = useLocation(); // contains email + code

    useEffect(() => {
        validatePassword(password);
    }, [password]);

    const validatePassword = (password) => {
        setPasswordValidations({
            uppercase: /[A-Z]/.test(password),
            lowercase: /[a-z]/.test(password),
            number: /\d/.test(password),
            specialChar: /[!@#$%^&*(),.?":{}|<>\-_]/.test(password),
            minLength: password.length >= 8,
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (password !== confirmPassword) {
            setError("Passwords must match.");
            return;
        }

        if (!Object.values(passwordValidations).every(Boolean)) {
            setError("Password does not meet all the requirements.");
            return;
        }

        try {
            setLoading(true);
            setError("");

            await axios.post("/v1/users/verify-forgot-password", {
                email: state?.email,
                code: state?.code,
                password,
            });

            navigate("/auth");
        } catch (err) {
            setLoading(false);
            setError(err?.response?.data?.message || "Something went wrong.");
        }
    };

    const renderValidationTag = (label, isValid) => (
        <span
            className={`px-2 py-1 text-xs flex rounded-full ${
                isValid
                    ? "bg-[#DCFDDB] text-[#1DCB1C] border border-[#1DCB1C]"
                    : "bg-[#EFF0FB] text-[#475467]"
            }`}
        >
            {isValid && <Tick className="mr-1" />} {label}
        </span>
    );

    return (
        <div className="bg-white rounded-xl w-[600px]">
            <div className="flex !pt-5">
                <NewPassword />
            </div>

            <div className="p-8 !pt-0">
                <h2 className="text-2xl font-bold text-gray-900">Create new password</h2>

                <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                    {/* New Password */}
                    <div className="relative">
                        <Input
                            type={showPassword ? "text" : "password"}
                            placeholder="New Password"
                            value={password}
                            onChange={(value) => setPassword(value)}
                        />
                        <button
                            type="button"
                            className="absolute right-3 top-3"
                            onClick={() => setShowPassword((prev) => !prev)}
                        >
                            {showPassword ? (
                                <EyeCloseIcon className="w-6 h-6" />
                            ) : (
                                <EyeIcon className="w-6 h-6" />
                            )}
                        </button>
                    </div>

                    {/* Validation tags */}
                    <div className="flex flex-wrap gap-2">
                        {renderValidationTag("Uppercase", passwordValidations.uppercase)}
                        {renderValidationTag("Lowercase", passwordValidations.lowercase)}
                        {renderValidationTag("Number", passwordValidations.number)}
                        {renderValidationTag("Special character", passwordValidations.specialChar)}
                        {renderValidationTag("8 characters", passwordValidations.minLength)}
                    </div>

                    {/* Confirm Password */}
                    <div className="relative">
                        <Input
                            type={showConfirmPassword ? "text" : "password"}
                            placeholder="Confirm Password"
                            value={confirmPassword}
                            onChange={(value) => setConfirmPassword(value)}
                        />
                        <button
                            type="button"
                            className="absolute right-3 top-3"
                            onClick={() => setShowConfirmPassword((prev) => !prev)}
                        >
                            {showConfirmPassword ? (
                                <EyeCloseIcon className="w-6 h-6" />
                            ) : (
                                <EyeIcon className="w-6 h-6" />
                            )}
                        </button>
                    </div>

                    {/* Match Check */}
                    {confirmPassword && (
                        <div className="text-sm mt-1">
                            {password === confirmPassword ? (
                                <p className="text-green-600 font-medium">✓ Passwords match</p>
                            ) : (
                                <p className="text-yellow-500 font-medium">
                                    ⚠ Passwords must match
                                </p>
                            )}
                        </div>
                    )}

                    <ErrorMessage message={error} />

                    <div className="flex justify-end">
                        <button
                            type="submit"
                            disabled={loading}
                            className={`bg-[#6F00FF] text-white py-2 px-6 rounded-lg mt-4 hover:bg-[#6f00fff0] transition ${
                                loading ? "opacity-50 cursor-not-allowed" : ""
                            }`}
                        >
                            {loading ? <Loader /> : "Submit"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateNewPassword;
